<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";

// Мнения экспертов в статье — без редактора.
//
// Сами одобренные мнения рендерит СЕРВЕР прямо в HTML статьи (ArticleController::
// injectExpertOpinions) — так их видит поисковик. Здесь только интерфейс:
//  - юристу — кнопка «Добавить мнение» под каждым подзаголовком и форма;
//  - автору статьи и админу — панель «Ожидают одобрения» с «Одобрить / Отклонить»;
//  - автору мнения — его мнения на модерации (и «Удалить»).
//
// Кнопки под подзаголовками вставляются в DOM после монтирования (тело статьи — v-html,
// своих компонентов внутри у него нет). На сервере их нет — краулеру они и не нужны.
const props = defineProps({
  opinions: { type: Object, default: () => ({ canAdd: false, canModerate: false, pending: [], min: 30, max: 800 }) },
  articleId: { type: Number, required: true },
  authId: { type: Number, default: null },
  toc: { type: Array, default: () => [] },
});

const sectionTitle = (anchor) => props.toc.find((t) => t.id === anchor)?.text || "раздел статьи";

// ── форма ──
const formOpen = ref(false);
const anchor = ref("");
const text = ref("");
const sending = ref(false);
const error = ref("");
const done = ref("");
const len = computed(() => text.value.trim().length);
const editingId = ref(null); // null — новое мнение, иначе правим существующее

function openForm(id) {
  editingId.value = null;
  text.value = "";
  anchor.value = id || props.toc[0]?.id || "";
  error.value = "";
  done.value = "";
  formOpen.value = true;
}

function openEdit(o) {
  editingId.value = o.id;
  anchor.value = o.anchor;
  text.value = o.text;
  error.value = "";
  done.value = "";
  formOpen.value = true;
}

async function call(url, method, body) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(token ? { "X-CSRF-TOKEN": token } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });
  let data = {};
  try { data = await res.json(); } catch (e) { /* пусто */ }
  if (!res.ok || !data.ok) throw new Error(data.error || "Не удалось сохранить, попробуйте ещё раз");
  return data;
}

async function submit() {
  error.value = "";
  if (len.value < props.opinions.min) { error.value = `Минимум ${props.opinions.min} знаков`; return; }
  if (len.value > props.opinions.max) { error.value = `Максимум ${props.opinions.max} знаков`; return; }
  sending.value = true;
  try {
    const r = editingId.value
      ? await call(`/article-opinions/${editingId.value}`, "PUT", { anchor: anchor.value, text: text.value })
      : await call(`/articles/${props.articleId}/opinions`, "POST", { anchor: anchor.value, text: text.value });
    text.value = "";
    if (r.status === "approved") {
      window.location.reload();
    } else {
      done.value = editingId.value
        ? "Изменения сохранены. Мнение снова появится после одобрения автором статьи."
        : "Спасибо! Мнение отправлено автору статьи и появится после одобрения.";
    }
  } catch (e) {
    error.value = e.message;
  } finally {
    sending.value = false;
  }
}

// ── модерация ──
const busy = ref(null);
async function moderate(id, action) {
  busy.value = id;
  try {
    await call(`/article-opinions/${id}/moderate`, "POST", { action });
    window.location.reload();
  } catch (e) {
    alert(e.message);
    busy.value = null;
  }
}
async function remove(id) {
  if (!confirm("Удалить мнение?")) return;
  busy.value = id;
  try {
    await call(`/article-opinions/${id}`, "DELETE");
    window.location.reload();
  } catch (e) {
    alert(e.message);
    busy.value = null;
  }
}

// ── кнопки под подзаголовками ──
const injected = [];
const mounted = ref(false); // модалка через Teleport — только в браузере, не в SSR
onMounted(async () => {
  mounted.value = true;
  await nextTick();

  // «Изменить / Удалить» на опубликованных карточках — их рендерит сервер в теле статьи
  (props.opinions.editable || []).forEach((o) => {
    const card = document.getElementById(`opinion-${o.id}`);
    if (!card) return;
    const bar = document.createElement("div");
    bar.className = "expert-opinion__actions";
    [["Изменить", () => openEdit(o)], ["Удалить", () => remove(o.id)]].forEach(([label, fn]) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = label;
      b.addEventListener("click", fn);
      bar.appendChild(b);
    });
    card.appendChild(bar);
    injected.push(bar);
  });

  if (!props.opinions.canAdd) return;
  props.toc.forEach((t) => {
    const h = document.getElementById(t.id);
    if (!h || !h.closest(".articlebody")) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "expert-opinion-add";
    btn.textContent = "💬 Добавить мнение эксперта";
    btn.addEventListener("click", () => openForm(t.id));
    h.insertAdjacentElement("afterend", btn);
    injected.push(btn);
  });
});
onUnmounted(() => injected.forEach((b) => b.remove()));
</script>

<template>
  <!-- Ожидают одобрения: модератору — все, юристу — свои -->
  <section v-if="opinions.pending?.length" class="my-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
    <h3 class="m-0 mb-3 text-base font-bold text-amber-900">
      {{ opinions.canModerate ? "Мнения экспертов ждут одобрения" : "Ваши мнения на модерации" }}
      <span class="font-normal text-amber-700">· {{ opinions.pending.length }}</span>
    </h3>
    <div v-for="o in opinions.pending" :key="o.id" class="mb-3 last:mb-0 rounded-lg bg-white p-3 border border-amber-100">
      <div class="text-xs text-gray-500 mb-1">
        <b class="text-gray-800">{{ o.name }}</b> · {{ o.date }} · к разделу «{{ sectionTitle(o.anchor) }}»
      </div>
      <p class="m-0 text-sm text-gray-700 whitespace-pre-line">{{ o.text }}</p>
      <div class="mt-2 flex flex-wrap gap-2">
        <template v-if="opinions.canModerate">
          <button type="button" :disabled="busy === o.id" @click="moderate(o.id, 'approve')"
            class="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            Одобрить
          </button>
          <button type="button" :disabled="busy === o.id" @click="moderate(o.id, 'reject')"
            class="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
            Отклонить
          </button>
        </template>
        <template v-if="opinions.canModerate || o.user_id === authId">
          <button type="button" :disabled="busy === o.id" @click="openEdit(o)"
            class="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
            Изменить
          </button>
          <button type="button" :disabled="busy === o.id" @click="remove(o.id)"
            class="px-3 py-1.5 rounded-lg border border-gray-300 text-rose-700 text-sm hover:bg-rose-50 disabled:opacity-50">
            Удалить
          </button>
        </template>
      </div>
    </div>
  </section>

  <!-- Форма мнения -->
  <Teleport v-if="mounted" to="body">
    <div v-if="formOpen" class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      @click.self="formOpen = false">
      <div class="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl p-5 shadow-xl">
        <div class="flex items-start justify-between gap-3">
          <h3 class="m-0 text-lg font-bold text-gray-900">{{ editingId ? "Изменить мнение" : "Мнение эксперта" }}</h3>
          <button type="button" @click="formOpen = false" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <p class="mt-1 mb-3 text-sm text-gray-500">
          Появится под разделом с вашим фото и ссылкой на профиль{{ opinions.autoPublish ? " сразу после отправки." : " — после одобрения автором статьи." }}
        </p>

        <label class="block text-sm font-medium text-gray-700 mb-1">Раздел</label>
        <select v-model="anchor" class="w-full rounded-lg border-gray-300 text-sm mb-3">
          <option v-for="t in toc" :key="t.id" :value="t.id">{{ t.text }}</option>
        </select>

        <label class="block text-sm font-medium text-gray-700 mb-1">Ваше мнение</label>
        <textarea v-model="text" rows="6" :maxlength="opinions.max"
          placeholder="Короткий комментарий по существу: практика, нюанс, с чем столкнётся читатель"
          class="w-full rounded-lg border-gray-300 text-sm"></textarea>
        <div class="flex justify-between text-xs mt-1" :class="len > opinions.max || (len && len < opinions.min) ? 'text-rose-600' : 'text-gray-400'">
          <span>от {{ opinions.min }} до {{ opinions.max }} знаков, без ссылок и рекламы</span>
          <span>{{ len }} / {{ opinions.max }}</span>
        </div>

        <p v-if="error" class="mt-2 text-sm text-rose-600">{{ error }}</p>
        <p v-if="done" class="mt-2 text-sm text-emerald-700">{{ done }}</p>

        <div class="mt-4 flex justify-end gap-2">
          <button type="button" @click="formOpen = false"
            class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50">Закрыть</button>
          <button v-if="!done" type="button" :disabled="sending" @click="submit"
            class="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {{ sending ? "Сохраняю…" : editingId ? "Сохранить" : "Отправить" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
